import React from "react";
import { 
  FiCalendar, 
  FiTag, 
  FiTrendingUp, 
  FiActivity 
} from "react-icons/fi";

export const formatValue = (value) => Number(value || 0).toFixed(2);

export const formatDashboardTitle = (name) =>
  name
    ?.toLowerCase()
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase()) || "Dashboard Detail";

export const emptyData = {
  cards: [
    { label: "Mondays In Month", value: "0", subtitle: "", color: "blue", iconName: "calendar" },
    { label: "Active SKUs", value: "0", subtitle: "", color: "indigo", iconName: "tag" },
    { label: "Total Qty", value: "0", subtitle: "", color: "emerald", iconName: "sigma" },
    { label: "Total Value", value: "0.00 Cr", subtitle: "", color: "orange", iconName: "rupee" },
    { label: "WoW Value Change", value: "0%", trend: "0%", isPositive: true, color: "yellow", iconName: "arrows" },
    { label: "MoM Value Change", value: "0%", trend: "0%", isPositive: true, color: "pink", iconName: "chart" },
  ],
  summaryTable1: {
    title: "Component-wise Value",
    subtitle: "Share of total value",
    totalValue: "0 Cr",
    totalPercent: "0%",
    rows: []
  },
  summaryTable2: {
    title: "Grouping basis — Nacelle",
    subtitle: "Component = Nacelle",
    totalQty: "0",
    totalValue: "0 Cr",
    rows: []
  },
  summaryTable3: {
    title: "Grouping basis — Tower",
    subtitle: "Component = Tower",
    totalQty: "0",
    totalValue: "0 Cr",
    rows: []
  },
  chart1: [],
  chart2: [],
  mainTable: {
    totalItems: 0,
    periods: [],
    prevPeriodLabel: "Prev Month Total",
    rows: [],
    totals: {
      values: [],
      prev: { qty: "0", value: "0" }
    },
    excelFiles: []
  }
};

export const colorClasses = {
  blue: { border: "border-l-blue-500", bg: "bg-blue-500" },
  indigo: { border: "border-l-indigo-600", bg: "bg-indigo-600" },
  emerald: { border: "border-l-emerald-500", bg: "bg-emerald-500" },
  orange: { border: "border-l-orange-500", bg: "bg-orange-500" },
  yellow: { border: "border-l-yellow-400", bg: "bg-yellow-400" },
  pink: { border: "border-l-pink-400", bg: "bg-pink-400" },
};

export const renderIcon = (name) => {
  switch(name) {
    case "calendar": return <FiCalendar className="w-3 h-3" />;
    case "tag": return <FiTag className="w-3 h-3" />;
    case "sigma": return <span className="font-bold text-xs">Σ</span>;
    case "rupee": return <span className="font-bold text-xs">₹</span>;
    case "arrows": return <FiTrendingUp className="w-3 h-3" />; 
    case "chart": return <FiActivity className="w-3 h-3" />;
    default: return <FiActivity className="w-3 h-3" />;
  }
};

export const formatNumber = (num) => {
  if (!num) return "0";
  return Number(num).toLocaleString('en-IN', { maximumFractionDigits: 2 });
};

export const formatCr = (num) => {
  if (!num) return "0.00 Cr";
  return (Number(num) / 10000000).toFixed(2) + " Cr";
};

export const formatLakhs = (num) => {
  if (!num) return "0.00 L";
  return (Number(num) / 100000).toFixed(2) + " L";
};

export const getColor = (comp) => {
  const c = (comp || "").toLowerCase();
  if (c.includes("nacelle fg")) return "#e1a600";
  if (c.includes("nacelle")) return "#e86a33";
  if (c.includes("tower")) return "#5443a3";
  if (c.includes("kps")) return "#3a7ada";
  if (c.includes("rotor")) return "#3bb175";
  return "#94a3b8";
};

export const buildDashboardData = (items) => {
  if (!items || !items.kpis) return emptyData;

  const { kpis, mainTable, summaryTables } = items;

  // KPIs
  const cards = [
    { label: "Mondays In Month", value: kpis.mondaysInMonth || 0, subtitle: "", color: "blue", iconName: "calendar" },
    { label: "Active SKUs", value: formatNumber(kpis.activeSkUs), subtitle: "", color: "indigo", iconName: "tag" },
    { label: "Total Qty", value: formatLakhs(kpis.totalQty), subtitle: "", color: "emerald", iconName: "sigma" },
    { label: "Total Value", value: kpis.totalValue || formatCr(kpis.totalValueLatest), subtitle: "", color: "orange", iconName: "rupee" },
    { label: "WoW Value Change", value: kpis.wowChange || "0%", trend: kpis.wowChange || "0%", isPositive: !(kpis.wowChange || "").startsWith("-"), color: "yellow", iconName: "arrows" },
    { label: "MoM Value Change", value: kpis.momChange || "0%", trend: kpis.momChange || "0%", isPositive: !(kpis.momChange || "").startsWith("-"), color: "pink", iconName: "chart" },
  ];

  // Summary Table 1
  const s1Rows = (summaryTables?.componentSummary || [])
    .filter(r => r.Component && r.Component.toLowerCase() !== "total")
    .map(r => ({
      label: r.Component || "—",
      value: formatCr(r.Value),
      percent: Number(r.Percentage || 0).toFixed(1),
      color: getColor(r.Component),
      bg: `bg-[#94a3b8]/10` // Will override with inline style below
    }));
  const s1TotalVal = summaryTables?.componentSummary?.[0]?.TotalValue;
  const summaryTable1 = {
    title: "Component-wise Value",
    subtitle: "Share of total value",
    totalValue: s1TotalVal ? formatCr(s1TotalVal) : "0 Cr",
    totalPercent: "100%",
    rows: s1Rows
  };

  // Summary Table 2 (Nacelle)
  const nGroup = summaryTables?.nacelleGrouping || [];
  const nTotalRow = nGroup.find(r => r.Grouping === "Total") || {};
  const nRows = nGroup.filter(r => r.Grouping !== "Total").map(r => ({
    label: r.Grouping || "(no grouping)",
    qty: formatNumber(r.Qty),
    value: formatCr(r.Value),
    percent: Number(r.Percentage || 0).toFixed(1)
  }));
  const summaryTable2 = {
    title: "Grouping basis — Nacelle",
    subtitle: "Component = Nacelle",
    totalQty: formatNumber(nTotalRow.Qty),
    totalValue: formatCr(nTotalRow.Value),
    rows: nRows
  };

  // Summary Table 3 (Tower)
  const tGroup = summaryTables?.towerGrouping || [];
  const tTotalRow = tGroup.find(r => r.Grouping === "Total") || {};
  const tRows = tGroup.filter(r => r.Grouping !== "Total").map(r => ({
    label: r.Grouping || "(no grouping)",
    qty: formatNumber(r.Qty),
    value: formatCr(r.Value),
    percent: Number(r.Percentage || 0).toFixed(1)
  }));
  const summaryTable3 = {
    title: "Grouping basis — Tower",
    subtitle: "Component = Tower",
    totalQty: formatNumber(tTotalRow.Qty),
    totalValue: formatCr(tTotalRow.Value),
    rows: tRows
  };

  // Charts
  const chart1 = (items.analytics?.valueByComponent || []).length > 0 
    ? items.analytics.valueByComponent.map(r => ({
        Component: r.Component,
        Value: parseFloat((r.TotalValue / 10000000).toFixed(2)),
        color: getColor(r.Component)
      }))
    : s1Rows.map(r => ({
        Component: r.label,
        Value: parseFloat(r.value.replace(/,/g, '')),
        color: r.color
      }));

  const chart2 = (items.analytics?.valueTrend || []).length > 0
    ? items.analytics.valueTrend.map(r => {
        const d = new Date(r.SnapDate);
        const dateStr = isNaN(d) ? r.SnapDate : d.toLocaleDateString("en-GB", { day: '2-digit', month: 'short', year: 'numeric' });
        return {
          Date: dateStr,
          Value: parseFloat((r.TotalValue / 10000000).toFixed(2)),
          Qty: parseFloat((r.TotalQty / 100000).toFixed(2)), // in Lakhs
          isSelectedMonth: r.IsCurrentMonth === 1
        };
      })
    : emptyData.chart2;

  // Main Table
  const headers = mainTable?.headers?.mondays || [];
  const mRows = (mainTable?.rows || []).map(r => {
    const values = headers.map((h, i) => {
      const mondayData = r.mondays?.[i] || {};
      return {
        qty: formatNumber(mondayData.qty),
        value: formatNumber(mondayData.value)
      };
    });
    return {
      component: r.component || "—",
      color: getColor(r.component),
      plant: r.plant || "—",
      material: r.material || "—",
      description: r.description || "—",
      grouping: r.grouping || "—",
      values,
      prev: { qty: formatNumber(r.prevMonth?.qty), value: formatNumber(r.prevMonth?.value) }
    };
  });

  const periods = headers.map(h => ({ label: h.label, isDemo: false }));
  
  const tableData = {
    totalItems: mainTable?.totals?.itemCount || mainTable?.rows?.length || 0,
    periods,
    prevPeriodLabel: mainTable?.headers?.prevMonth?.label || "Prev Month Total",
    rows: mRows,
    totals: {
      values: (mainTable?.totals?.mondays || []).map(m => ({ 
        qty: formatLakhs(m.qty), 
        value: formatCr(m.value) 
      })),
      prev: { 
        qty: formatLakhs(mainTable?.totals?.prevMonth?.qty), 
        value: formatCr(mainTable?.totals?.prevMonth?.value) 
      }
    },
    excelFiles: items.excelFiles || emptyData.mainTable.excelFiles
  };

  return {
    cards,
    summaryTable1,
    summaryTable2,
    summaryTable3,
    chart1,
    chart2,
    mainTable: tableData
  };
};
